import React, { useEffect, useState } from 'react';
import './AccountMenu.scss';
import ModalForm from './Modal';
import Button from './Button';
import { SessionDetails, ShowingDetails } from '../models/api';
import { getQR } from '../services/session.service';
import Feedback from './Feedback';
import LoadingSpinner from './LoadingSpinner';
import { FaDownload } from 'react-icons/fa';

type Props = {
  session: SessionDetails;
  close: () => void;
  isPreview?: boolean | null;
  previewShowing?: ShowingDetails | null;
};

const SessionSharingDetails: React.FC<Props> = ({
  session,
  close,
  isPreview,
  previewShowing
}) => {
  const [sessionQR, setSessionQR] = useState<string>();
  const [loading, setLoading] = useState<boolean | null>();
  const [errors, setErrors] = useState<string[]>([]);

  const getSession = async () => {
    if (loading) {
      return;
    }
    setLoading(true);
    try {
      const [qrResponse] = await Promise.all([
        getQR(session.reference, previewShowing?.reference)
      ]);
      if (qrResponse) {
        setSessionQR(URL.createObjectURL(qrResponse));
        setErrors([]);
      } else {
        setErrors(['There was a problem getting the QR code']);
      }
    } catch (error) {
      setErrors(['There was a problem getting the QR code']);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSession();
  }, [session]);

  const handleDownload = () => {
    if (!sessionQR) {
      return;
    }
    const link = document.createElement('a');
    link.href = sessionQR;
    link.download = `qr-${session.name ?? 'event'}`;

    // Append to the DOM, click, then remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <ModalForm>
      <form className="content-form sharing-form">
        <Feedback messages={errors} type="error" />
        {!loading && sessionQR && (
          <>
            <a
              href={previewShowing?.previewLink ?? session.publicLink}
              target="_blank"
              rel="noreferrer"
              className="link-qr"
            >
              <img
                src={sessionQR}
                width={200}
                height={200}
                alt="Scan the QR code to open session"
              />
            </a>
            {isPreview && (
              <div className="preview">Link to the preview only</div>
            )}
            {!isPreview && (
              <>
                <div>
                  Share this QR code with your audience, so they can scan it and
                  access this event.
                </div>
                <div className="button-container single-button">
                  <Button onClick={() => handleDownload()}>
                    Download <FaDownload />
                  </Button>
                </div>
              </>
            )}
          </>
        )}
        {!!loading && (
          <div className="qr-placeholder">
            <LoadingSpinner inline={true} />
          </div>
        )}
        {!isPreview && session.accessCode && (
          <>
            <hr />
            <div>
              Alternatively, use this access code at{' '}
              <a
                href="https://watch.vieweventnotes.com"
                target="_blank"
                rel="noreferrer"
              >
                watch.vieweventnotes.com
              </a>
            </div>
            <div>
              Access code: <strong>{session.accessCode}</strong>
            </div>
          </>
        )}
        <div className="button-container single-button">
          <Button onClick={() => close()} type="Tertiary">
            Close
          </Button>
        </div>
      </form>
    </ModalForm>
  );
};

export default SessionSharingDetails;
